import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { User, Mail, Building, Crown, Save, Eye, EyeOff } from "lucide-react";

function Perfil() {
    const { user, fetchUserProfile } = useAuth();
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const [formData, setFormData] = useState({
        nombre: user?.nombre || "",
        email: user?.email || "",
        password: "",
        confirmPassword: "",
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setMessage({ type: "", text: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: "", text: "" });

        // Validar contraseñas si se está cambiando
        if (
            formData.password &&
            formData.password !== formData.confirmPassword
        ) {
            setMessage({ type: "error", text: "Las contraseñas no coinciden" });
            setLoading(false);
            return;
        }

        try {
            const updateData = {
                nombre: formData.nombre,
                email: formData.email,
            };

            // Solo incluir password si se proporcionó
            if (formData.password) {
                updateData.password = formData.password;
            }

            await axios.put(`/api/users/${user.id}`, updateData);

            // Refrescar perfil del usuario
            if (fetchUserProfile) {
                await fetchUserProfile();
            }

            setEditing(false);
            setFormData({
                ...formData,
                password: "",
                confirmPassword: "",
            });
            setMessage({
                type: "success",
                text: "Perfil actualizado exitosamente",
            });
        } catch (error) {
            console.error("Error al actualizar perfil:", error);
            setMessage({
                type: "error",
                text:
                    error.response?.data?.message ||
                    "Error al actualizar el perfil",
            });
        } finally {
            setLoading(false);
        }
    };

    const cancelEdit = () => {
        setEditing(false);
        setFormData({
            nombre: user?.nombre || "",
            email: user?.email || "",
            password: "",
            confirmPassword: "",
        });
        setMessage({ type: "", text: "" });
    };

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString("es-ES", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    return (
        <div className="container">
            <div className="page-header">
                <h1>Mi Perfil</h1>
                <p>Gestiona tu información personal</p>
            </div>

            <div className="perfil-container">
                <div className="perfil-card">
                    <div className="perfil-avatar">
                        <div className={`avatar-icon ${user?.tipo_usuario}`}>
                            {user?.tipo_usuario === "gestor" ? (
                                <Crown size={32} />
                            ) : (
                                <User size={32} />
                            )}
                        </div>
                        <div className="avatar-info">
                            <h2>{user?.nombre}</h2>
                            <span
                                className={`badge ${
                                    user?.tipo_usuario === "gestor"
                                        ? "badge-warning"
                                        : "badge-success"
                                }`}
                            >
                                {user?.tipo_usuario === "gestor"
                                    ? "Gestor"
                                    : "Inquilino"}
                            </span>
                        </div>
                    </div>

                    {message.text && (
                        <div className={`alert alert-${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="perfil-form">
                        <div className="form-section">
                            <h3>Información Personal</h3>

                            <div className="form-group">
                                <label className="form-label">
                                    <User size={16} />
                                    Nombre Completo
                                </label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    className="form-input"
                                    disabled={!editing}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <Mail size={16} />
                                    Email
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input"
                                    disabled={!editing}
                                    required
                                />
                            </div>

                            {user?.habitacion_numero && (
                                <div className="form-group">
                                    <label className="form-label">
                                        <Building size={16} />
                                        Habitación
                                    </label>
                                    <input
                                        type="text"
                                        value={user.habitacion_numero}
                                        className="form-input"
                                        disabled
                                    />
                                </div>
                            )}
                        </div>

                        {editing && (
                            <div className="form-section">
                                <h3>Cambiar Contraseña</h3>
                                <p className="section-description">
                                    Deja estos campos vacíos si no quieres
                                    cambiar tu contraseña
                                </p>

                                <div className="form-group">
                                    <label className="form-label">
                                        Nueva Contraseña
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder="Nueva contraseña"
                                            minLength="6"
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff size={16} />
                                            ) : (
                                                <Eye size={16} />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Confirmar Contraseña
                                    </label>
                                    <input
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="Confirmar nueva contraseña"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-section">
                            <h3>Información de Cuenta</h3>

                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">
                                        Fecha de Registro
                                    </span>
                                    <span className="info-value">
                                        {user?.fecha_creacion
                                            ? formatearFecha(
                                                  user.fecha_creacion
                                              )
                                            : "No disponible"}
                                    </span>
                                </div>

                                <div className="info-item">
                                    <span className="info-label">
                                        Tipo de Usuario
                                    </span>
                                    <span className="info-value">
                                        {user?.tipo_usuario === "gestor"
                                            ? "Gestor del Piso"
                                            : "Inquilino"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            {!editing ? (
                                <button
                                    type="button"
                                    onClick={() => setEditing(true)}
                                    className="btn btn-primary"
                                >
                                    Editar Perfil
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={cancelEdit}
                                        className="btn btn-outline"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="spinner-small"></div>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                Guardar Cambios
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                .page-header {
                    margin-bottom: 32px;
                }

                .page-header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 8px;
                }

                .page-header p {
                    color: #6b7280;
                }

                .perfil-container {
                    max-width: 600px;
                    margin: 0 auto;
                }

                .perfil-card {
                    background: white;
                    border-radius: 12px;
                    padding: 32px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }

                .perfil-avatar {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 32px;
                    padding-bottom: 24px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .avatar-icon {
                    width: 80px;
                    height: 80px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .avatar-icon.gestor {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                }

                .avatar-icon.inquilino {
                    background: linear-gradient(135deg, #10b981, #059669);
                }

                .avatar-info h2 {
                    font-size: 24px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 8px;
                }

                .perfil-form {
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                }

                .form-section {
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 24px;
                }

                .form-section:last-of-type {
                    border-bottom: none;
                    padding-bottom: 0;
                }

                .form-section h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 16px;
                }

                .section-description {
                    color: #6b7280;
                    font-size: 14px;
                    margin-bottom: 20px;
                }

                .form-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 6px;
                    font-weight: 500;
                    color: #374151;
                }

                .form-input:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                    cursor: not-allowed;
                }

                .password-input-container {
                    position: relative;
                }

                .password-toggle {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                }

                .password-toggle:hover {
                    color: #374151;
                    background-color: #f3f4f6;
                }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                }

                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .info-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #6b7280;
                }

                .info-value {
                    font-size: 16px;
                    color: #1f2937;
                }

                .form-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                    padding-top: 24px;
                    border-top: 1px solid #e5e7eb;
                }

                .spinner-small {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @media (max-width: 768px) {
                    .perfil-card {
                        padding: 24px;
                    }

                    .perfil-avatar {
                        flex-direction: column;
                        text-align: center;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .info-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
}

export default Perfil;
